import type { AdminModule } from '../api/admin';

// Which modules a client user may reach (client_users.module_scope, migration 0078). null = every
// module the client has enabled, including ones switched on later. The API enforces it; this only
// shapes what the admin screen offers and how it reads the stored value back.

// Staff-only whatever the client has switched on, so never something to grant a client user.
const STAFF_ONLY = new Set(['tech-inventory', 'pr-review']);

export interface ModuleOption { code: string; name: string }

export function parseScope(raw: string | null | undefined): string[] | null {
  return raw === null || raw === undefined ? null : raw.split(',').map((c) => c.trim()).filter(Boolean);
}

/** What the screen lists: the client's enabled modules a client user could ever see, then any code a
 *  scope names that is not among them (so a stale entry stays visible and can be unticked). */
export function moduleOptions(modules: AdminModule[], scope: string[] | null): ModuleOption[] {
  const out: ModuleOption[] = modules
    .filter((m) => m.enabled && !STAFF_ONLY.has(m.code))
    .map((m) => ({ code: m.code, name: m.staff_only ? `${m.name} (staff preview)` : m.name }));
  for (const code of scope ?? []) if (!out.some((o) => o.code === code)) out.push({ code, name: `${code} (not enabled)` });
  return out;
}

/** Tick or untick one module. Returns null when that would leave the user with nothing, which the
 *  API refuses; the box for the last module is disabled so this is a second line of defence. */
export function toggleScope(scope: string[], code: string): string[] | null {
  const next = scope.includes(code) ? scope.filter((c) => c !== code) : [...scope, code];
  return next.length ? next : null;
}

export function scopeSummary(scope: string[] | null, options: ModuleOption[]): string {
  if (scope === null) return 'Everything enabled';
  if (!scope.length) return 'Nothing';
  return scope.map((c) => options.find((o) => o.code === c)?.name.replace(/ \(.*\)$/, '') ?? c).join(', ');
}
